import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { PriceTable } from '@lemoncloud/codes-goods-api';
import { CatalogView } from '@lemoncloud/codes-goods-api';
import { CardView } from '@lemoncloud/lemon-payments-api';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useProductService } from '@web/app/features/home/hooks';
import {
    CardRegistrationForm,
    CatalogImageSlider,
    Editor,
    OrderProductButton,
    OrderProductDialog,
    PaymentConfirmDialog,
    useSelectedStore,
    useStereoIcon,
} from '@web/app/shared';
import { createYooptaEditor } from '@yoopta/editor';
import { markdown } from '@yoopta/exports';
import { AlertTriangle, ChevronLeft, ChevronRight, Github, RefreshCw, X } from 'lucide-react';

import { useFetchCatalogById } from '@codes/catalog';
import { useCards } from '@codes/payments';
import { getCurrencySymbol } from '@codes/react-utils';
import {
    Avatar,
    AvatarImage,
    Badge,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Label,
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    Skeleton,
} from '@codes/ui-kit';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

export const CatalogDetailPage = () => {
    const { catalogId } = useParams<{ catalogId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { i18n } = useTranslation();
    const [isOrderDialogOpen, setIsOrderDialogOpen] = React.useState(false);
    const [isCardRegistrationOpen, setIsCardRegistrationOpen] = React.useState(false);
    const [isPaymentConfirmOpen, setIsPaymentConfirmOpen] = React.useState(false);
    const getStereoIcon = useStereoIcon();

    const { data: cardsData } = useCards({ stereo: 'stripe', view: 'mine' });
    const { workspace } = useSelectedStore();

    const {
        data: currentCatalog,
        isFetching: isCatalogFetching,
        error: fetchCatalogError,
        refetch: refetchCatalog,
        isRefetching: isCatalogRefetching,
    } = useFetchCatalogById(catalogId!);

    const yooptaEditor = useMemo(() => createYooptaEditor(), []);
    const yooptaEnEditor = useMemo(() => createYooptaEditor(), []);

    const { myProducts } = useProductService();

    useEffect(() => {
        const deserializeMarkdown = (editor: any, markdownString: string) => {
            const value = markdown.deserialize(editor, markdownString);
            editor.setEditorValue(value);
        };

        if (!currentCatalog) {
            return;
        }

        if (currentCatalog.readme) {
            deserializeMarkdown(yooptaEditor, currentCatalog.readme);
        }

        if (currentCatalog.readmeEn) {
            deserializeMarkdown(yooptaEnEditor, currentCatalog.readmeEn);
        }
    }, [currentCatalog, yooptaEditor, yooptaEnEditor]);

    const textRef = useRef<HTMLParagraphElement>(null);
    const noticeRef = useRef<HTMLUListElement>(null);

    const priceKeys: (keyof PriceTable)[] = ['initPrice', 'trafficPrice', 'hourPerPrice', 'callPerPrice'];

    const contentList = [t('guide.callbackNotice'), t('guide.approvalNotice'), t('guide.cancellationNotice')];

    const handleAddServiceClick = () => {
        if (!workspace) {
            return;
        }

        const hasDuplicateProductRequest = !!myProducts.find(product => product.catalogId === currentCatalog?.id);

        if (hasDuplicateProductRequest) {
            toast({ title: t('serviceRequest.duplicatedErrorMsgText') });
            return;
        }

        if (workspace.stereo === 'basic') {
            setIsOrderDialogOpen(true);
            return;
        }

        const hasNoCard = cardsData?.total === 0;

        if (hasNoCard) {
            setIsPaymentConfirmOpen(true);
            return;
        }

        const hasNoSecret = !workspace?.secret$ || !workspace?.secret$?.id;

        if (hasNoSecret) {
            // TODO: 모달창 퍼블리싱 후 처리 예정
            alert('Access key is not registered.');
            return;
        }

        setIsOrderDialogOpen(true);
    };

    const getIconImage = (catalog: CatalogView | undefined) => {
        if (!catalog) {
            return '/assets/icons/empty.svg';
        }
        const thumb = catalog.thumbs && catalog.thumbs.length >= 1 ? catalog.thumbs[0] : null;

        if (thumb) {
            return thumb;
        }

        return getStereoIcon(catalog.stereo as string);
    };

    const DependencyList = ({ sliceIdx = 0 }: { sliceIdx?: number }) => {
        if (!currentCatalog || !currentCatalog.dependency$$ || currentCatalog.dependency$$.length === 0) {
            return null;
        }

        const slicedDependencies =
            sliceIdx > 0 ? currentCatalog.dependency$$.slice(0, sliceIdx) : currentCatalog.dependency$$;

        return (
            <ul className="grid max-xs:grid-cols-1 grid-cols-2 gap-2">
                {slicedDependencies.map(dependency => (
                    <li key={dependency.id}>
                        <Sheet>
                            <SheetTrigger asChild>
                                <button className="bg-card border h-[201px] w-full shadow-custom rounded-lg flex flex-col px-4 pt-[14px] pb-[17px] hover:border-gradient">
                                    <Avatar>
                                        <AvatarImage src={getIconImage(dependency as CatalogView)} />
                                    </Avatar>
                                    <div className="font-medium text-start truncate min-h-[25px]">
                                        {dependency.name}
                                    </div>
                                    <div className="text-sm text-sub max-h-[76px] overflow-auto text-start mt-[5px]">
                                        {(i18n.language === 'en' && dependency?.summaryEn) || dependency?.summary}
                                    </div>
                                    <div className="flex pt-1 flex-1 items-end">
                                        <Badge variant="B">{dependency?.stereo}</Badge>
                                    </div>
                                </button>
                            </SheetTrigger>
                            <SheetContent className="overflow-auto">
                                <SheetHeader>
                                    <SheetTitle>{dependency.name}</SheetTitle>
                                    <SheetClose>
                                        <X className="w-5 h-5" />
                                    </SheetClose>
                                </SheetHeader>
                                <div className="mt-4">
                                    <CatalogImageSlider
                                        images={dependency?.images}
                                        name={dependency?.name}
                                        stereo={dependency?.stereo}
                                        iconImage={getIconImage(dependency as CatalogView)}
                                    />
                                </div>

                                <div className="flex flex-col gap-y-2 mt-[18px]">
                                    <div className="flex items-center gap-x-2">
                                        <Avatar>
                                            <AvatarImage
                                                src={getIconImage(dependency as CatalogView)}
                                                darkSrc={getIconImage(dependency as CatalogView)}
                                            />
                                        </Avatar>
                                        <div className="font-medium text-start">{dependency.name}</div>
                                    </div>
                                    <SheetDescription className="text-start text-sub text-sm">
                                        {(i18n.language === 'en' && dependency?.summaryEn) || dependency?.summary}
                                    </SheetDescription>
                                </div>

                                <div className="w-full h-[1px] bg-gray-200 my-7"></div>

                                {/* Configuration Information */}
                                {dependency.config$ && Object.keys(dependency.config$).length > 0 && (
                                    <>
                                        <Label className="text-[16px] mb-2">{t('serviceDetail.configuration')}</Label>
                                        <div className="bg-box rounded-lg p-3 mb-4">
                                            {dependency.config$.runtime && (
                                                <div className="flex justify-between items-center text-sm mb-2">
                                                    <span className="text-sub">{t('serviceDetail.runtime')}:</span>
                                                    <span>{dependency.config$.runtime}</span>
                                                </div>
                                            )}
                                            {dependency.config$.region && (
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-sub">{t('serviceDetail.region')}:</span>
                                                    <span>{dependency.config$.region}</span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Price Information */}
                                {dependency.price$ && (
                                    <>
                                        <Label className="text-[16px] mb-2">{t('serviceDetail.serviceFee')}</Label>
                                        <div className="bg-box rounded-lg p-3 mb-4">
                                            {dependency.price$.initPrice !== undefined && (
                                                <div className="flex justify-between items-center text-sm mb-2">
                                                    <span className="text-sub">
                                                        {t('price.service.initial_setup_fee')}
                                                    </span>
                                                    <span>
                                                        {getCurrencySymbol(dependency.price$.currency)}
                                                        {dependency.price$.initPrice}
                                                    </span>
                                                </div>
                                            )}
                                            {dependency.price$.trafficPrice !== undefined && (
                                                <div className="flex justify-between items-center text-sm mb-2">
                                                    <span className="text-sub">{t('price.service.traffic_cost')}</span>
                                                    <span>
                                                        {getCurrencySymbol(dependency.price$.currency)}
                                                        {dependency.price$.trafficPrice}
                                                    </span>
                                                </div>
                                            )}
                                            {dependency.price$.hourPerPrice !== undefined && (
                                                <div className="flex justify-between items-center text-sm mb-2">
                                                    <span className="text-sub">{t('price.service.cost_per_hour')}</span>
                                                    <span>
                                                        {getCurrencySymbol(dependency.price$.currency)}
                                                        {dependency.price$.hourPerPrice}
                                                    </span>
                                                </div>
                                            )}
                                            {dependency.price$.callPerPrice !== undefined && (
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-sub">{t('price.service.per_call')}</span>
                                                    <span>
                                                        {getCurrencySymbol(dependency.price$.currency)}
                                                        {dependency.price$.callPerPrice}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Settings/Environment Variables */}
                                {dependency.setting$ && dependency.setting$.length > 0 && (
                                    <>
                                        <Label className="text-[16px] mb-2">{t('serviceDetail.settings')}</Label>
                                        <div className="bg-box rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
                                            {dependency.setting$
                                                .filter(setting => !setting.isHidden)
                                                .slice(0, 5)
                                                .map((setting, index) => (
                                                    <div key={index} className="text-sm mb-2 last:mb-0">
                                                        <div className="font-medium text-sub">{setting.name}</div>
                                                        <div className="text-xs text-placeholder">
                                                            {setting.description}
                                                        </div>
                                                    </div>
                                                ))}
                                            {dependency.setting$.filter(setting => !setting.isHidden).length > 5 && (
                                                <div className="text-xs text-placeholder mt-2">
                                                    {t('serviceDetail.moreSettings', {
                                                        count:
                                                            dependency.setting$.filter(setting => !setting.isHidden)
                                                                .length - 5,
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </SheetContent>
                        </Sheet>
                    </li>
                ))}
            </ul>
        );
    };

    if (!catalogId) {
        return (
            <div className="flex flex-col py-[34px] px-[68px] max-md:p-5 w-full items-center">
                <div className="w-full max-w-[500px]">
                    <div className="flex flex-col items-center gap-6 py-12">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('serviceDetail.empty.title')}</h3>
                            <p className="text-gray-600">{t('serviceDetail.empty.description')}</p>
                        </div>
                        <Button onClick={() => navigate(-1)} className="gap-2">
                            <ChevronLeft className="w-4 h-4" />
                            {t('common.back')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (isCatalogFetching || isCatalogRefetching) {
        return (
            <div className="flex flex-col py-[34px] px-[68px] max-md:p-5 w-full items-center">
                <div className="w-full max-w-[500px]">
                    <div className="flex items-center gap-2 mb-[42px]">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <Skeleton className="h-6 w-48" />
                    </div>

                    <div className="space-y-7">
                        <Skeleton className="w-full h-48" />
                        <div className="flex items-center gap-2">
                            <Skeleton className="w-[50px] h-[50px] rounded-full" />
                            <div className="flex flex-col flex-1 gap-2">
                                <Skeleton className="h-6 w-20" />
                                <Skeleton className="h-5 w-32" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-4 w-3/4" />
                        </div>
                        <div className="space-y-4">
                            <Skeleton className="h-6 w-40" />
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-5/6" />
                                <Skeleton className="h-4 w-4/5" />
                            </div>
                        </div>
                        <Skeleton className="h-12 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    if (fetchCatalogError) {
        return (
            <div className="flex flex-col py-[34px] px-[68px] max-md:p-5 w-full items-center">
                <div className="w-full max-w-[500px]">
                    <div className="flex items-center gap-2 mb-[42px]">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <div className="text-[22px] font-semibold">{t('serviceDetail.title')}</div>
                    </div>

                    <div className="flex flex-col items-center gap-6 py-12">
                        <div className="w-16 h-16 rounded-full bg-error/15 flex items-center justify-center">
                            <AlertTriangle className="w-8 h-8 text-error" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {t('serviceDetail.error.loadFailed')}
                            </h3>
                            <p className="text-gray-600">{t('serviceDetail.error.tryAgain')}</p>
                        </div>
                        <Button onClick={() => refetchCatalog()} disabled={isCatalogRefetching} className="gap-2">
                            <RefreshCw className={`w-4 h-4 ${isCatalogRefetching ? 'animate-spin' : ''}`} />
                            {t('serviceDetail.error.tryAgain')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col py-[34px] px-[68px] max-md:p-5 w-full items-center">
                <div className="w-full max-w-[500px]">
                    <div className="flex items-center gap-2 mb-[42px]">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <div className="text-[22px] font-semibold">
                            {(i18n.language === 'en' && currentCatalog?.nameEn) || currentCatalog?.name}
                        </div>
                    </div>

                    <div className="space-y-7">
                        <CatalogImageSlider
                            images={currentCatalog?.images}
                            name={currentCatalog?.name}
                            stereo={currentCatalog?.stereo}
                            iconImage={getIconImage(currentCatalog as CatalogView)}
                        />
                        <div className="flex items-center gap-2">
                            <Avatar className="w-[50px] h-[50px]">
                                <AvatarImage
                                    src={getIconImage(currentCatalog as CatalogView)}
                                    darkSrc={getIconImage(currentCatalog as CatalogView)}
                                    alt={currentCatalog?.stereo}
                                />
                            </Avatar>
                            <div className="flex flex-col flex-1">
                                <Badge className="text-sm text-gray-800 capitalize self-start h-[25px]">
                                    {t(currentCatalog?.isDomestic ? 'serviceDetail.domestic' : 'serviceDetail.global')}
                                </Badge>
                                <div className="text-[18px] font-medium">
                                    {(i18n.language === 'en' && currentCatalog?.nameEn) || currentCatalog?.name}
                                </div>
                            </div>
                            {currentCatalog?.github$?.url && (
                                <a
                                    href={currentCatalog.github$.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center w-8 h-8 rounded-full bg-primary transition-all duration-200 hover:scale-110 group"
                                    title="View on GitHub"
                                >
                                    <Github className="w-4 h-4 text-white transition-transform duration-200 group-hover:rotate-12" />
                                </a>
                            )}
                        </div>
                        <div className="text-gray-900 break-words text-sm">
                            {(i18n.language === 'en' && currentCatalog?.summaryEn) || currentCatalog.summary}
                        </div>

                        {currentCatalog?.dependency$$ && currentCatalog.dependency$$.length > 0 && (
                            <>
                                <div className="w-full h-[1px] bg-gray-200"></div>
                                <div className="flex flex-col gap-y-2">
                                    <div className="flex justify-between items-center">
                                        <div className="text-[18px] font-medium">Package Deal</div>
                                        {currentCatalog.dependency$$.length > 4 && (
                                            <Sheet>
                                                <SheetTrigger asChild>
                                                    <button className="text-[13px] flex items-center text-sub">
                                                        View All <ChevronRight className="w-4 h-4" />
                                                    </button>
                                                </SheetTrigger>
                                                <SheetContent className="w-[500px] max-md:w-full overflow-auto">
                                                    <div className="flex justify-between mb-4">
                                                        <SheetTitle>Package Deal View All</SheetTitle>
                                                        <SheetClose asChild className="">
                                                            <button className="w-[26px] h-[26px] flex items-center justify-center rounded-full bg-button-disabled">
                                                                <X className="w-[18px] h-[18px]" />
                                                            </button>
                                                        </SheetClose>
                                                    </div>
                                                    <DependencyList />
                                                </SheetContent>
                                            </Sheet>
                                        )}
                                    </div>

                                    <DependencyList sliceIdx={4} />
                                </div>
                            </>
                        )}

                        <div className="w-full h-[1px] bg-gray-200"></div>
                        <div>
                            <div className="text-[18px] mb-4 font-medium">{t('serviceDetail.overview')}</div>
                            <p ref={textRef} className="text-sub break-words">
                                {(i18n.language === 'en' && currentCatalog?.descEn) || currentCatalog.desc}
                            </p>
                        </div>

                        {(() => {
                            const isEnglish = i18n.language === 'en';
                            const hasEnglishReadme = !!currentCatalog?.readmeEn;

                            if (isEnglish && hasEnglishReadme) {
                                return (
                                    <>
                                        <div className="w-full h-[1px] bg-gray-200"></div>
                                        <div>
                                            <Label className="text-[18px] mb-2 font-medium">README</Label>
                                            <Editor editor={yooptaEnEditor} readOnly={true} />
                                        </div>
                                    </>
                                );
                            }

                            return (
                                currentCatalog?.readme && (
                                    <>
                                        <div className="w-full h-[1px] bg-gray-200"></div>
                                        <div>
                                            <Label className="text-[18px] mb-4 font-medium">README</Label>
                                            <Editor editor={yooptaEditor} readOnly={true} />
                                        </div>
                                    </>
                                )
                            );
                        })()}

                        <div className="w-full h-[1px] bg-gray-200"></div>
                        {currentCatalog?.price$ ? (
                            <div>
                                <Label className="text-[18px] mb-4 font-medium">{t('serviceDetail.serviceFee')}</Label>
                                <div className="space-y-[17px]">
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-base font-medium text-gray-700">
                                            {t('price.service.monthly_subscription_fee')}
                                        </div>
                                        <span className="text-lg font-semibold text-gray-black">
                                            {getCurrencySymbol(currentCatalog?.price$.currency)}
                                            {
                                                // TODO:
                                                '-'
                                            }
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-base font-medium text-gray-700">
                                            {t('price.service.initial_setup_fee')}
                                        </div>
                                        <span className="text-lg font-semibold text-gray-black">
                                            {getCurrencySymbol(currentCatalog?.price$.currency)}
                                            {currentCatalog?.price$?.initPrice}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-base font-medium text-gray-700">
                                            {t('price.service.usage_fee')}
                                        </div>
                                        <span className="text-lg font-semibold text-gray-black">
                                            {getCurrencySymbol(currentCatalog?.price$.currency)}
                                            {
                                                // TODO:
                                                '-'
                                            }
                                        </span>
                                    </div>
                                </div>
                                <div className="bg-gray-50 py-[6px] px-2 rounded-[11px] space-y-[2px] mt-[9px]">
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-[15px] text-[#357AFF] font-medium">
                                            {t('price.service.traffic_cost')}
                                        </div>
                                        <span className="text-base text-gray-800 font-semibold">
                                            {getCurrencySymbol(currentCatalog?.price$.currency)}
                                            {currentCatalog?.price$?.trafficPrice}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-[15px] text-[#357AFF] font-medium">
                                            {t('price.service.cost_per_hour')}
                                        </div>
                                        <span className="text-base text-gray-800 font-semibold">
                                            {getCurrencySymbol(currentCatalog?.price$.currency)}
                                            {currentCatalog?.price$?.hourPerPrice}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center font-medium">
                                        <div className="text-[15px] text-[#357AFF] font-medium">
                                            {t('price.service.per_call')}
                                        </div>
                                        <span className="text-base text-gray-800 font-semibold">
                                            {getCurrencySymbol(currentCatalog?.price$.currency)}
                                            {currentCatalog?.price$?.callPerPrice}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center text-sub">{t('serviceDetail.noPrice')}</div>
                        )}

                        <div className="w-full h-[1px] bg-gray-200"></div>
                        <div>
                            <div className="text-sm mb-[10px] font-medium text-gray-600">
                                {t('serviceDetail.serviceGuidelines')}
                            </div>
                            <ul ref={noticeRef} className="flex flex-col gap-[3px]">
                                {contentList.map((content, index) => (
                                    <li key={index} className="relative pl-2 text-xs text-placeholder">
                                        <span className="absolute left-0 top-1/2 transform -translate-y-1/2 w-[3px] h-[3px] bg-outline-text rounded-full"></span>
                                        {content}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="pb-20">{/* Spacer for fixed button */}</div>
                    </div>
                </div>
            </div>

            {/* Fixed bottom button */}
            <div className="fixed bottom-0 left-16 max-md:left-0 right-0 bg-white dark:bg-[#252525] border-t border-gray-200 p-6 z-50">
                <div className="flex justify-center">
                    <div className="w-full max-w-[500px]">
                        <OrderProductButton onAddServiceClick={handleAddServiceClick} />
                    </div>
                </div>
            </div>

            <OrderProductDialog
                catalog={currentCatalog}
                isOpen={isOrderDialogOpen}
                onOpenChange={setIsOrderDialogOpen}
            />

            <PaymentConfirmDialog
                isOpen={isPaymentConfirmOpen}
                onOpenChange={setIsPaymentConfirmOpen}
                onConfirm={() => setIsCardRegistrationOpen(true)}
            />

            <Dialog open={isCardRegistrationOpen} onOpenChange={setIsCardRegistrationOpen}>
                <DialogContent className="max-w-[500px] px-6">
                    <DialogTitle className="mb-7 text-[20px] text-text">{t('myPage.registerCard')}</DialogTitle>
                    <Elements stripe={stripePromise}>
                        <CardRegistrationForm
                            onSuccess={(createdCard: CardView) => {
                                // TODO: do something
                                console.log(createdCard);
                                setIsCardRegistrationOpen(false);
                                setIsOrderDialogOpen(true);
                            }}
                        />
                    </Elements>
                </DialogContent>
            </Dialog>
        </>
    );
};
